import { useRef, useEffect, useState, useCallback } from "react";
import { useDisclosure } from "@mantine/hooks";

const scrollStyles = ["scroll", "auto"];
function findScrollParent(element: HTMLElement | null): HTMLElement | null {
    const parent = element?.parentElement;
    // if parent is null there is no scroll element, so return window
    if (!parent) return null;
    // can be modified to include also overflowX
    const { overflowY } = getComputedStyle(parent);
    // if parent has either overflowY scroll or auto return parent
    if (scrollStyles.includes(overflowY)) return parent;
    // otherwise continue search
    return findScrollParent(parent);
}

export const useScrollPositionPreservation = (
    shouldRestore: boolean
): [(ref: HTMLElement | null) => void, { enable: VoidFunction; disable: VoidFunction }] => {
    const scrollParentRef = useRef<HTMLDivElement | null>(null);
    const [enabled, handlers] = useDisclosure(false);
    const [scrollRestorationData, setScrollRestorationData] = useState<{
        scrollTop: number;
        scrollHeight: number;
    } | null>(null);

    const enable = useCallback(() => {
        if (scrollParentRef.current) {
            setScrollRestorationData({
                scrollTop: scrollParentRef.current.scrollTop,
                scrollHeight: scrollParentRef.current.scrollHeight,
            });
            handlers.open();
        }
    }, [handlers]);

    useEffect(() => {
        if (enabled && scrollParentRef.current && scrollRestorationData !== null && shouldRestore) {
            const { scrollTop, scrollHeight } = scrollRestorationData;
            const newScrollData = scrollTop + scrollParentRef.current.scrollHeight - scrollHeight;
            scrollParentRef.current.scroll({ top: newScrollData, behavior: "smooth" });
            handlers.close();
        }
    }, [scrollParentRef, scrollRestorationData, enabled, handlers, shouldRestore]);

    return [
        (ref: HTMLElement | null) => {
            if (ref !== null) {
                const scrollParent = findScrollParent(ref);
                if (scrollParent !== null) {
                    scrollParentRef.current = scrollParent as HTMLDivElement;
                }
            }
            return null;
        },
        {
            enable,
            disable: handlers.close,
        },
    ];
};
